/** The Advisor's words for wire values, and the small rules its screens share. */

import type { AdvisorOutcome, AdvisorRun, AdvisorRunStatus } from '@/api/types';
import type { BadgeTone } from '@/ui/Badge';

export const STATUS_LABELS: Record<AdvisorRunStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  succeeded: 'Finished',
  failed: 'Failed',
  over_budget: 'Over budget',
};

export const STATUS_TONES: Record<AdvisorRunStatus, BadgeTone> = {
  queued: 'neutral',
  running: 'info',
  succeeded: 'success',
  failed: 'danger',
  over_budget: 'warning',
};

export const STATUS_OPTIONS = (Object.keys(STATUS_LABELS) as AdvisorRunStatus[]).map((value) => ({
  value,
  label: STATUS_LABELS[value],
}));

/** A run's status in words; one the screen does not know reads as itself. */
export function statusLabel(status: string): string {
  return new Map<string, string>(Object.entries(STATUS_LABELS)).get(status) ?? status;
}

export function statusTone(status: string): BadgeTone {
  return new Map<string, BadgeTone>(Object.entries(STATUS_TONES)).get(status) ?? 'neutral';
}

export const OUTCOME_LABELS: Record<AdvisorOutcome, string> = {
  used_in_proposal: 'Used in a proposal',
  shared_with_client: 'Shared with the client',
  won: 'Won',
  lost: 'Lost',
};

export const VERDICT_LABELS: Record<string, string> = {
  strong: 'Strong fit',
  partial: 'Partial fit',
  weak: 'Weak fit',
};

export const CLAIMS_LABELS: Record<string, string> = {
  measured: 'Claims are measured',
  expected: 'Claims are expected, not measured',
  mixed: 'Some claims measured, some expected',
  none: 'No claims to cite',
};

/** What the status line says while a run works or once it stopped short. */
export function statusLine(run: Pick<AdvisorRun, 'status' | 'error'>): string | null {
  switch (run.status) {
    case 'queued':
      return 'Queued: the run starts in a moment. This page updates on its own.';
    case 'running':
      return 'Running: reading the inputs, then screening every published asset. This page updates on its own.';
    case 'failed':
      return run.error ?? 'The run failed.';
    case 'over_budget':
      return 'The monthly AI budget is used up, so this run stopped. Try again next month.';
    default:
      return null;
  }
}

export function verdictLabel(verdict: string | null | undefined): string | null {
  return verdict ? (VERDICT_LABELS[verdict] ?? verdict) : null;
}

export function claimsLabel(value: string | null | undefined): string | null {
  return value ? (CLAIMS_LABELS[value] ?? value) : null;
}

/** "Not recorded" for a missing person, as the other screens say it. */
export function personName(person: { display_name: string } | null): string {
  return person?.display_name ?? 'Not recorded';
}

/** What the page says when the confirmed client brief no longer reads as the brief does now. */
export function briefChangedText(run: AdvisorRun): string {
  return run.can.confirm_client_brief
    ? 'Content changed since you confirmed; confirm again.'
    : 'Content changed since it was confirmed. The PDF still prints the brief as confirmed.';
}
